import { useEffect, useMemo, useState } from 'react';
import api from '../api';

export default function AdminLurkerLogs() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [filters, setFilters] = useState({
    platform: '',
    event: '',
    severity: '',
    userId: '',
    entryId: '',
  });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(0);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const offset = page * limit;

  const [snapshotAt, setSnapshotAt] = useState('');
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState('');
  const [snapshot, setSnapshot] = useState(null);

  const canLoadLogs = useMemo(() => !!settings, [settings]);

  const loadSettings = async () => {
    setError('');
    const { data } = await api.get('/lurker-logs/settings');
    setSettings(data);
  };

  const loadLogs = async () => {
    if (!canLoadLogs) return;
    setError('');
    const params = {
      limit,
      offset,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(filters.platform ? { platform: filters.platform } : {}),
      ...(filters.event ? { event: filters.event } : {}),
      ...(filters.severity ? { severity: filters.severity } : {}),
      ...(filters.userId ? { userId: filters.userId } : {}),
      ...(filters.entryId ? { entryId: filters.entryId } : {}),
    };
    const { data } = await api.get('/lurker-logs/logs', { params });
    setLogs(data.items || []);
    setTotal(Number(data.total || 0));
  };

  const loadSnapshot = async () => {
    setSnapshotLoading(true);
    setSnapshotError('');
    try {
      const params = snapshotAt ? { at: snapshotAt } : {};
      const { data } = await api.get('/lurker2/admin/snapshot', { params });
      setSnapshot(data);
    } catch (e) {
      setSnapshotError(e?.response?.data?.error || e?.message || 'Konnte Snapshot nicht laden');
      setSnapshot(null);
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadSettings();
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || 'Konnte Settings nicht laden');
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadLogs();
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || 'Konnte Logs nicht laden');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadLogs, limit, offset, from, to, filters.platform, filters.event, filters.severity, filters.userId, filters.entryId]);

  const onSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError('');
    setOk('');
    try {
      const payload = {
        enabled: !!settings.enabled,
        mode: settings.mode,
        retentionDays: settings.retentionDays,
      };
      const { data } = await api.put('/lurker-logs/settings', payload);
      setSettings(data);
      setOk('Gespeichert.');
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const maxPage = useMemo(() => {
    if (!total) return 0;
    return Math.max(0, Math.ceil(total / limit) - 1);
  }, [total, limit]);

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Lurker‑Logs</h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>
          Diagnose-Logging für den Lurker: aktivieren, Aufbewahrung einstellen und Ereignisse auswerten.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {error && <div style={{ color: 'tomato', marginBottom: 10 }}>{error}</div>}
        {ok && <div style={{ color: '#b7ffb7', marginBottom: 10 }}>{ok}</div>}

        {snapshotError && <div style={{ color: 'tomato', marginBottom: 10 }}>{snapshotError}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 16 }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>Logging Einstellungen</h3>
            {!settings && <div>Lade…</div>}
            {settings && (
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!settings.enabled}
                    onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                  />
                  Logging aktiv
                </label>

                <label>
                  <div style={{ fontSize: 12, color: '#bbb' }}>Modus</div>
                  <select
                    value={settings.mode || 'errors'}
                    onChange={(e) => setSettings({ ...settings, mode: e.target.value })}
                  >
                    <option value="errors">Nur Fehler/Warnungen</option>
                    <option value="all">Alle Events</option>
                  </select>
                </label>

                <label>
                  <div style={{ fontSize: 12, color: '#bbb' }}>Aufbewahrung (Tage)</div>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={settings.retentionDays ?? 30}
                    onChange={(e) => setSettings({ ...settings, retentionDays: parseInt(e.target.value || '30', 10) })}
                  />
                  <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>Standard: 30 Tage</div>
                </label>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={onSave} disabled={saving}>{saving ? 'Speichere…' : 'Speichern'}</button>
                  <button onClick={() => { setOk(''); loadSettings().catch(() => {}); }}>Neu laden</button>
                </div>
              </div>
            )}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>Lurker 2.0 · Snapshot</h3>
            <div style={{ fontSize: 12, color: '#bbb', marginBottom: 10 }}>
              Zeigt, wer zum Zeitpunkt <b>at</b> den Lurker offen hatte und welchen Stream er zugeteilt bekommen hat.
              Grundlage sind OPEN/HEARTBEAT/CLOSE/ASSIGN Events (Heartbeat ca. 1×/Minute).
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
              <label style={{ minWidth: 260 }}>
                <div style={{ fontSize: 12, color: '#bbb' }}>at (ISO, optional)</div>
                <input value={snapshotAt} onChange={(e) => setSnapshotAt(e.target.value)} placeholder="2026-03-10T12:34:56Z" />
              </label>
              <button onClick={() => loadSnapshot().catch(() => {})} disabled={snapshotLoading}>{snapshotLoading ? 'Lade…' : 'Snapshot laden'}</button>
              <button onClick={() => { setSnapshotAt(''); loadSnapshot().catch(() => {}); }} disabled={snapshotLoading}>Jetzt</button>
            </div>

            {!snapshot && !snapshotLoading && <div style={{ color: '#bbb' }}>Noch kein Snapshot geladen.</div>}

            {snapshot && (
              <>
                <div style={{ fontSize: 12, color: '#bbb', marginBottom: 8 }}>
                  Zeitpunkt: {snapshot.at ? new Date(snapshot.at).toLocaleString() : ''} · Active: {snapshot.activeCount || 0}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#bbb' }}>Counts pro Stream</div>
                    <div style={{ fontSize: 12 }}>
                      {snapshot.byAssignedLogin && Object.keys(snapshot.byAssignedLogin).length ? (
                        Object.entries(snapshot.byAssignedLogin)
                          .sort((a, b) => (b[1] || 0) - (a[1] || 0) || String(a[0]).localeCompare(String(b[0])))
                          .map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                              <span style={{ color: '#ddd' }}>{k}</span>
                              <span style={{ color: '#bbb' }}>{v}</span>
                            </div>
                          ))
                      ) : (
                        <div style={{ color: '#bbb' }}>(keine)</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#bbb' }}>Hinweis</div>
                    <div style={{ fontSize: 12, color: '#bbb' }}>
                      Ein Session gilt als aktiv, wenn innerhalb der TTL ein OPEN/HEARTBEAT gesehen wurde und kein CLOSE danach.
                    </div>
                  </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                        <th style={{ padding: '6px 8px' }}>User</th>
                        <th style={{ padding: '6px 8px' }}>Page</th>
                        <th style={{ padding: '6px 8px' }}>Session</th>
                        <th style={{ padding: '6px 8px' }}>Window</th>
                        <th style={{ padding: '6px 8px' }}>Assigned</th>
                        <th style={{ padding: '6px 8px' }}>AssignedAt</th>
                        <th style={{ padding: '6px 8px' }}>LastSeen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(snapshot.sessions || []).map((s) => {
                        const u = snapshot.viewers ? snapshot.viewers[s.viewerUserId] : null;
                        const userLabel = u ? (u.displayName || u.username || String(s.viewerUserId)) : String(s.viewerUserId || '');
                        const sid = String(s.sessionId || '');
                        const shortSid = sid ? `${sid.slice(0, 8)}…${sid.slice(-4)}` : '';
                        return (
                          <tr key={`${s.viewerUserId}:${s.sessionId}:${s.page}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <td style={{ padding: '6px 8px' }}>{userLabel}</td>
                            <td style={{ padding: '6px 8px', color: '#bbb' }}>{s.page || ''}</td>
                            <td style={{ padding: '6px 8px', color: '#bbb', whiteSpace: 'nowrap' }} title={sid}>{shortSid}</td>
                            <td style={{ padding: '6px 8px', color: '#bbb' }}>{s.windowKey || ''}</td>
                            <td style={{ padding: '6px 8px' }}>{s.assignedLogin || ''}</td>
                            <td style={{ padding: '6px 8px', color: '#bbb', whiteSpace: 'nowrap' }}>{s.assignedAt ? new Date(s.assignedAt).toLocaleString() : ''}</td>
                            <td style={{ padding: '6px 8px', color: '#bbb', whiteSpace: 'nowrap' }}>{s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString() : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>Logs</h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Plattform</div>
                <select value={filters.platform} onChange={(e) => { setPage(0); setFilters({ ...filters, platform: e.target.value }); }}>
                  <option value="">(alle)</option>
                  <option value="twitch">twitch</option>
                  <option value="youtube">youtube</option>
                  <option value="kick">kick</option>
                  <option value="facebook">facebook</option>
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Severity</div>
                <select value={filters.severity} onChange={(e) => { setPage(0); setFilters({ ...filters, severity: e.target.value }); }}>
                  <option value="">(alle)</option>
                  <option value="info">info</option>
                  <option value="warn">warn</option>
                  <option value="error">error</option>
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Event</div>
                <input value={filters.event} onChange={(e) => { setPage(0); setFilters({ ...filters, event: e.target.value }); }} placeholder="z.B. TWITCH_READY_TIMEOUT" />
              </label>

              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>UserId</div>
                <input value={filters.userId} onChange={(e) => { setPage(0); setFilters({ ...filters, userId: e.target.value }); }} placeholder="#" />
              </label>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>EntryId</div>
                <input value={filters.entryId} onChange={(e) => { setPage(0); setFilters({ ...filters, entryId: e.target.value }); }} placeholder="#" />
              </label>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Limit</div>
                <select value={limit} onChange={(e) => { setPage(0); setLimit(parseInt(e.target.value, 10)); }}>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
              </label>

              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Von (ISO)</div>
                <input value={from} onChange={(e) => { setPage(0); setFrom(e.target.value); }} placeholder="2026-01-01" />
              </label>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Bis (ISO)</div>
                <input value={to} onChange={(e) => { setPage(0); setTo(e.target.value); }} placeholder="2026-01-31" />
              </label>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <button onClick={() => loadLogs().catch(() => {})}>Aktualisieren</button>
                <button onClick={() => { setPage(0); setFrom(''); setTo(''); setFilters({ platform: '', event: '', severity: '', userId: '', entryId: '' }); }}>Reset</button>
              </div>
            </div>

            <div style={{ fontSize: 12, color: '#bbb', marginBottom: 8 }}>
              {total ? `Gesamt: ${total} · Seite ${page + 1} / ${maxPage + 1}` : 'Keine Logs'}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                    <th style={{ padding: '6px 8px' }}>Zeit</th>
                    <th style={{ padding: '6px 8px' }}>User</th>
                    <th style={{ padding: '6px 8px' }}>Entry</th>
                    <th style={{ padding: '6px 8px' }}>Plattform</th>
                    <th style={{ padding: '6px 8px' }}>Event</th>
                    <th style={{ padding: '6px 8px' }}>Sev</th>
                    <th style={{ padding: '6px 8px' }}>Host</th>
                    <th style={{ padding: '6px 8px' }}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{l.createdAt ? new Date(l.createdAt).toLocaleString() : ''}</td>
                      <td style={{ padding: '6px 8px' }}>{l.userId ?? ''}</td>
                      <td style={{ padding: '6px 8px' }}>{l.entryId ?? ''}</td>
                      <td style={{ padding: '6px 8px' }}>{l.platform}</td>
                      <td style={{ padding: '6px 8px' }}>{l.event}</td>
                      <td style={{ padding: '6px 8px', color: l.severity === 'error' ? 'tomato' : (l.severity === 'warn' ? '#ffd27d' : '#ddd') }}>{l.severity}</td>
                      <td style={{ padding: '6px 8px' }}>{l.hostname || ''}</td>
                      <td style={{ padding: '6px 8px' }} title={l.message || ''}>{l.message || ''}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: 10, color: '#bbb' }}>Keine Einträge.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page <= 0}>Zurück</button>
              <button onClick={() => setPage((p) => Math.min(maxPage, p + 1))} disabled={page >= maxPage}>Weiter</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
